import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, ArrowLeft, Send, Coffee, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function WaiterNewOrder() {
    const { t, lang } = useApp();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [tableNo, setTableNo] = useState(params.get("table") || "");
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [cart, setCart] = useState({});           // {id: {item, qty}}  — NEW items only
    const [existing, setExisting] = useState(null); // existing order (if any) for this table
    const [notes, setNotes] = useState("");
    const [sending, setSending] = useState(false);

    // load catalog
    useEffect(() => {
        Promise.all([api.get("/categories"), api.get("/menu", { params: { available_only: true } })]).then(([c, m]) => {
            setCats(c.data);
            setItems(m.data);
            if (c.data[0]) setActiveCat(c.data[0].id);
        });
    }, []);

    // detect existing order for the chosen table
    const fetchExisting = async (tn) => {
        if (!tn) { setExisting(null); return; }
        try {
            const r = await api.get("/orders");
            const found = r.data.find(
                (o) => Number(o.table_number) === Number(tn)
                    && ["pending", "confirmed", "delivered", "on_hold"].includes(o.status)
            );
            setExisting(found || null);
        } catch (e) { setExisting(null); }
    };

    useEffect(() => { fetchExisting(tableNo); /* eslint-disable-next-line */ }, [tableNo]);

    const filtered = items.filter((i) => i.category_id === activeCat);

    const add = (it) => {
        setCart((c) => {
            const x = c[it.id] || { item: it, qty: 0 };
            return { ...c, [it.id]: { ...x, qty: x.qty + 1 } };
        });
    };

    const sub = (id) => {
        setCart((c) => {
            const x = c[id];
            if (!x) return c;
            if (x.qty <= 1) {
                const { [id]: _, ...rest } = c;
                return rest;
            }
            return { ...c, [id]: { ...x, qty: x.qty - 1 } };
        });
    };

    const totals = useMemo(() => {
        const newItems = Object.values(cart);
        const newSubtotal = newItems.reduce((a, x) => a + x.item.price * x.qty, 0);
        const newCount = newItems.reduce((a, x) => a + x.qty, 0);
        const existingSubtotal = existing ? (existing.subtotal || 0) : 0;
        return {
            newSubtotal,
            newCount,
            existingSubtotal,
            combinedSubtotal: existingSubtotal + newSubtotal,
        };
    }, [cart, existing]);

    const send = async () => {
        const tn = Number(tableNo);
        if (!tn || tn < 1 || tn > 70) { toast.error(t("error") + ": " + t("table_number")); return; }
        if (totals.newCount === 0) { toast.error(t("no_items")); return; }
        setSending(true);
        try {
            const newOrderItems = Object.values(cart).map((x) => ({
                menu_item_id: x.item.id,
                name_en: x.item.name_en,
                name_ar: x.item.name_ar,
                price: x.item.price,
                qty: x.qty,
                notes: "",
            }));
            if (existing) {
                // merge: existing items + new items (kept separate so cashier sees each line)
                const merged = [...existing.items, ...newOrderItems];
                await api.patch(`/orders/${existing.id}`, { items: merged, notes: notes || existing.notes });
                toast.success(t("items_added"));
            } else {
                await api.post("/orders", { table_number: tn, notes, items: newOrderItems });
                toast.success(t("order_sent"));
            }
            navigate("/waiter");
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-4 pb-44" data-testid="waiter-new-order">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate("/waiter")} data-testid="back-waiter"><ArrowLeft className="h-5 w-5 flip-rtl" /></Button>
                <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-2xl flex-1`}>
                    {existing ? t("add_to_order") : t("new_order")}
                </h1>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">{t("table_number")}</label>
                    <Input type="number" min="1" max="70" value={tableNo} onChange={(e) => setTableNo(e.target.value)} className="w-20 text-center text-lg" data-testid="order-table-input" />
                </div>
            </div>

            {/* Existing order banner */}
            {existing && (
                <Card className="grain border-primary/30 bg-primary/5" data-testid="existing-order-card">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-primary">
                                <ClipboardList className="h-4 w-4" />
                                {t("order_no")} #{existing.order_no} · {t("existing_items")}
                            </div>
                            <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 text-primary">{t(existing.status)}</span>
                        </div>
                        <div className="space-y-1 text-sm">
                            {existing.items.map((it, i) => (
                                <div key={i} className="flex justify-between text-foreground/80">
                                    <span><span className="text-muted-foreground tabular-nums me-2">{it.qty}×</span>{lang === "ar" ? it.name_ar : it.name_en}</span>
                                    <span className="tabular-nums text-muted-foreground">{fmtSDG(it.price * it.qty, lang)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between pt-2 mt-2 border-t border-border text-sm">
                            <span className="text-muted-foreground">{t("subtotal")}</span>
                            <span className="tabular-nums font-medium">{fmtSDG(existing.subtotal, lang)}</span>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Tabs value={activeCat || ""} onValueChange={setActiveCat}>
                <TabsList className="flex flex-wrap gap-1 h-auto bg-transparent">
                    {cats.map((c) => (
                        <TabsTrigger key={c.id} value={c.id} data-testid={`order-cat-${c.id}`} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                            {lang === "ar" ? c.name_ar : c.name_en}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filtered.map((it) => {
                    const inCart = cart[it.id];
                    return (
                        <Card key={it.id} className="grain bg-card/70 overflow-hidden" data-testid={`order-item-${it.id}`}>
                            <div className="aspect-[4/3] bg-secondary">
                                {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> :
                                    <div className="flex items-center justify-center h-full"><Coffee className="h-10 w-10 text-primary/30" /></div>}
                            </div>
                            <CardContent className="p-3">
                                <div className="font-medium text-sm leading-tight">{lang === "ar" ? it.name_ar : it.name_en}</div>
                                <div className="text-primary text-sm tabular-nums mt-1">{fmtSDG(it.price, lang)}</div>
                                {inCart ? (
                                    <div className="flex items-center justify-between mt-2">
                                        <Button size="icon" variant="outline" onClick={() => sub(it.id)} data-testid={`item-minus-${it.id}`}><Minus className="h-3.5 w-3.5" /></Button>
                                        <span className="font-medium tabular-nums">{inCart.qty}</span>
                                        <Button size="icon" onClick={() => add(it)} data-testid={`item-plus-${it.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                                    </div>
                                ) : (
                                    <Button size="sm" className="w-full mt-2" onClick={() => add(it)} data-testid={`item-add-${it.id}`}><Plus className="h-3.5 w-3.5 me-1" />{t("add")}</Button>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Cart sticky bottom */}
            <div className="fixed bottom-0 inset-x-0 bg-card border-t border-border p-4 z-30">
                <div className="max-w-5xl mx-auto">
                    {existing && totals.newCount > 0 && (
                        <div className="text-xs text-muted-foreground mb-2 flex justify-between">
                            <span>{t("new_items")}: {totals.newCount} · {fmtSDG(totals.newSubtotal, lang)}</span>
                            <span>{t("total")}: <span className="text-primary font-medium tabular-nums">{fmtSDG(totals.combinedSubtotal, lang)}</span></span>
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-2 gap-3">
                        <div className="text-sm">
                            <span className="text-muted-foreground">{t("items")}: </span><span className="font-medium">{totals.newCount}</span>
                            {!existing && (
                                <>
                                    <span className="text-muted-foreground ms-4">{t("total")}: </span>
                                    <span className="font-medium text-primary tabular-nums">{fmtSDG(totals.newSubtotal, lang)}</span>
                                </>
                            )}
                        </div>
                        <Button size="lg" disabled={totals.newCount === 0 || sending} onClick={send} data-testid="send-order-btn">
                            <Send className="h-4 w-4 me-2" />
                            {existing ? t("add_to_order") : t("send_to_cashier")}
                        </Button>
                    </div>
                    {totals.newCount > 0 && (
                        <Textarea rows={1} placeholder={t("notes")} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" data-testid="order-notes" />
                    )}
                </div>
            </div>
        </div>
    );
}
